import {
	convertArbitraryValueForTrackingId,
	generateTrackingId,
	isNotNilOrEmpty
} from "client/Misc/utils";
import { ReactElement, useCallback } from "react";
import { ActionListHeader } from "../ActionList/ActionListHeader";
import { ItemResponsiveBehavior } from "../CollapsibleList/CollapsibleList";
import {
	InlineOptionSelector,
	InlineOptionSelectorItem,
	InlineOptionSelectorProps
} from "../InlineOptionSelector/InlineOptionSelector";
import { ToolbarItem } from "./ToolbarItem";
import { ToolbarPriority } from "./Toolbar";
import styles from "./Toolbar.module.less";
import { ActionListItem } from "../ActionList/ActionListItem";

export type ToolbarInlineOptionSelectorProps<TValue> = InlineOptionSelectorProps<TValue> & {
	menuTitle?: string;
	responsiveBehavior?: ItemResponsiveBehavior;
};

export const ToolbarInlineOptionSelector = <TValue,>(props: ToolbarInlineOptionSelectorProps<TValue>): ReactElement => {
	const {
		menuTitle,
		responsiveBehavior,
		...restOfTheProps
	} = props;

	const renderNormalMode = (): ReactElement => {
		return (
			<div className={styles.inlineOptionSelector}>
				{menuTitle}
				<InlineOptionSelector
					{...restOfTheProps}
					menuTitle={menuTitle}
				/>
			</div>
		);
	};

	const renderCollapsedMode = (): ReactElement => {
		return (
			<CollapsedInlineOptionSelector
				{...restOfTheProps}
				menuTitle={menuTitle}
			/>
		);
	};

	return (
		<ToolbarItem
			isGroup
			responsiveBehavior={responsiveBehavior}
			renderNormalMode={renderNormalMode}
			renderCollapsedMode={renderCollapsedMode}
			priority={ToolbarPriority.Default}
		/>
	);
};
ToolbarInlineOptionSelector.displayName = "Toolbar.InlineOptionSelector";

export type CollapsedInlineOptionSelectorProps<TValue> = InlineOptionSelectorProps<TValue> & {
	label?: string;
};

export const CollapsedInlineOptionSelector = <TValue,>(props: CollapsedInlineOptionSelectorProps<TValue>): ReactElement => {
	const {
		menuTitle,
		onChange,
		options,
		value,
		trackingId
	} = props;

	const getItemValueTrackingId = useCallback(
		(item: InlineOptionSelectorItem<TValue>): string => {
			if (isNotNilOrEmpty(item.valueTrackingId)) {
				return item.valueTrackingId;
			}

			return convertArbitraryValueForTrackingId(item.value);
		},
		[]
	);

	return (
		<>
			{isNotNilOrEmpty(menuTitle) && (
				<ActionListHeader
					text={menuTitle}
					trackingId={generateTrackingId(trackingId, "__label")}
				/>
			)}
			{options.map((item, index) => {
				const isSelected: boolean = item.value === value;

				return (
					<ActionListItem
						key={index}
						isActive={isSelected}
						rightIcon={isSelected ? "check" : undefined}
						text={item.text}
						onClick={() => onChange(item.value)}
						trackingId={generateTrackingId(trackingId, `__item|${getItemValueTrackingId(item)}`)}
					/>
				);
			})}
		</>
	);
};
CollapsedInlineOptionSelector.displayName = "CollapsedInlineOptionSelector";